import { useAuth } from './auth';
import type { IntegrationProvider, SyncFrequency } from './types';

export type BillingFeature = 'FACILITIES' | 'GOOGLE_PROVISIONING' | 'BLACKBAUD_BILLING' | 'FREQUENT_SYNC' | 'DEVICE_LOCATION';
export type BillingItemKey = 'PLATFORM' | BillingFeature;
export type SubscriptionStatus = 'INCOMPLETE' | 'INCOMPLETE_EXPIRED' | 'TRIALING' | 'ACTIVE' | 'PAST_DUE' | 'CANCELED' | 'UNPAID' | 'PAUSED';
/** Why a school has what it has: billing isn't enforced, Edvora's arrangements, or a subscription. */
export type BillingBasis = 'UNMETERED' | 'COMPLIMENTARY' | 'TRIAL' | 'SUBSCRIPTION' | 'NONE';
/** A trial Edvora gives includes everything; a school's own sign-up trial includes Tech and Facilities, with manual syncs. */
export type TrialKind = 'FULL' | 'SIGN_UP';

/** A renewal that failed and hasn't been paid: the monthly subscription, or a yearly add-on's own one. */
export interface PaymentIssue {
  item: 'PLATFORM' | BillingFeature;
  status: SubscriptionStatus;
  dueSince: string;
  /** When access ends (or ended) unless it's paid. */
  accessEndsAt: string;
  lapsed: boolean;
  /** Stripe's page to pay the invoice; none once Stripe canceled the subscription. */
  invoiceUrl: string | null;
  /** In cents. */
  amountDue: number | null;
}

/** What the signed-in school's plan includes, from /auth/me. */
export interface PlanSummary {
  enforced: boolean;
  basis: BillingBasis;
  platform: boolean;
  features: Record<BillingFeature, boolean>;
  /** Roster and device integrations sync on their schedules (a sign-up trial syncs them on demand; mailboxes always run). */
  scheduledSyncs: boolean;
  trialEndsAt: string | null;
  trialKind: TrialKind;
  /** When the school is deleted if it doesn't subscribe (it signed up itself); null otherwise. */
  deletesAt: string | null;
  /** Unpaid renewals (only for administrators). */
  paymentIssues: PaymentIssue[];
  /** Schools can subscribe through Stripe on this environment. */
  stripe: boolean;
}

export interface CatalogItem {
  key: BillingItemKey;
  name: string;
  description: string;
  /** In cents: the school's band, for an add-on sold in bands. */
  amount: number;
  currency: string;
  interval: 'month' | 'year';
  /** Sold in bands: the school's Chromebooks, and the most this band covers (null above the largest band sold). */
  tier: { devices: number | null; upTo: number | null } | null;
}

/** A yearly add-on a school has: its own subscription, its own year. */
export interface AnnualAddOn {
  feature: BillingFeature;
  status: SubscriptionStatus | null;
  periodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  /** For an add-on sold in bands: the band it's on, and the fleet it was bought for. */
  upTo: number | null;
  devices: number | null;
}

export interface BillingOverview extends Omit<PlanSummary, 'stripe' | 'scheduledSyncs'> {
  configured: boolean;
  subscription: { status: SubscriptionStatus | null; addOns: BillingFeature[]; currentPeriodEnd: string | null; cancelAtPeriodEnd: boolean } | null;
  annualAddOns: AnnualAddOn[];
  complimentary: boolean;
  trialEndsAt: string | null;
  grantedAddOns: BillingFeature[];
  hasCustomer: boolean;
  catalog: CatalogItem[];
  retentionDays: number;
  salesEmail: string;
  graceDays: number;
}

/** A school's billing, as Edvora's platform admins see it on the Schools list. */
export interface PlatformBilling {
  /** What the complimentary plan and the trial include; empty means everything. */
  complimentaryModules?: BillingModule[];
  trialModules?: BillingModule[];
  subscriptionStatus: SubscriptionStatus | null;
  addOns: BillingFeature[];
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  annualAddOns: AnnualAddOn[];
  complimentary: boolean;
  trialEndsAt: string | null;
  trialKind: TrialKind;
  grantedAddOns: BillingFeature[];
  notes: string | null;
  stripeCustomerId: string | null;
  deleteIfUnpaid: boolean;
  deletesAt: string | null;
}

export const BILLING_FEATURES: BillingFeature[] = ['FACILITIES', 'GOOGLE_PROVISIONING', 'BLACKBAUD_BILLING', 'FREQUENT_SYNC', 'DEVICE_LOCATION'];

/** What a school loses while a yearly add-on's renewal is unpaid, for the wording of the notice. */
export const ADD_ON_CONSEQUENCE: Record<BillingFeature, string> = {
  FACILITIES: 'using Facilities',
  GOOGLE_PROVISIONING: 'syncing Google accounts',
  BLACKBAUD_BILLING: 'posting damage invoices to Blackbaud',
  FREQUENT_SYNC: 'syncing every hour or every 6 hours',
  DEVICE_LOCATION: 'finding the school’s Chromebooks',
};

export const FEATURE_LABEL: Record<BillingFeature, string> = {
  FACILITIES: 'Facilities module',
  GOOGLE_PROVISIONING: 'Google Workspace Provisioning',
  BLACKBAUD_BILLING: 'Blackbaud Billing Management',
  FREQUENT_SYNC: 'Frequent Integration Sync',
  DEVICE_LOCATION: 'Chromebook Location & Recovery',
};

export const SUBSCRIPTION_STATUS_LABEL: Record<SubscriptionStatus, string> = {
  INCOMPLETE: 'Waiting for payment',
  INCOMPLETE_EXPIRED: 'Payment not completed',
  TRIALING: 'Trial',
  ACTIVE: 'Active',
  PAST_DUE: 'Past due',
  CANCELED: 'Canceled',
  UNPAID: 'Unpaid',
  PAUSED: 'Paused',
};

export const TRIAL_KIND_LABEL: Record<TrialKind, string> = {
  FULL: 'Everything',
  SIGN_UP: 'Tech and Facilities, manual roster and device syncs (sign-up trial)',
};

/** What Edvora can include in a trial or a complimentary plan: the Tech module, then the add-ons. */
export type BillingModule = 'PLATFORM' | BillingFeature;
export const BILLING_MODULES: BillingModule[] = ['PLATFORM', ...BILLING_FEATURES];
export const MODULE_LABEL: Record<BillingModule, string> = { PLATFORM: 'Tech module', ...FEATURE_LABEL };
export const MODULE_DESCRIPTION: Record<BillingModule, string> = {
  PLATFORM: 'Assets, checkouts and loaners, the technology help desk, integrations, and the technician app',
  FACILITIES: 'Maintenance requests and work orders, preventive maintenance, inspections, equipment, and documents',
  GOOGLE_PROVISIONING: 'Google accounts created, moved, and suspended from Blackbaud',
  BLACKBAUD_BILLING: 'Damage invoices posted to families’ Blackbaud bills',
  FREQUENT_SYNC: 'Hourly and 6-hour syncs, and mailboxes checked every minute',
  DEVICE_LOCATION: 'Where the school’s Chromebooks are, with geofences and missing mode',
};

/** Edvora's Stripe account, for the platform console. */
export interface StripeCustomerRow {
  id: string;
  name: string | null;
  email: string | null;
  school: { id: string; name: string; slug: string; subscriptionStatus: SubscriptionStatus | null } | null;
  balance: number;
  openInvoices: number;
  outstanding: number;
  currency: string;
  delinquent: boolean;
  createdAt: string;
}

export interface StripeInvoiceRow {
  id: string;
  number: string | null;
  status: string | null;
  customerId: string | null;
  customerName: string | null;
  schoolName: string | null;
  amountDue: number;
  amountRemaining: number;
  currency: string;
  createdAt: string | null;
  dueAt: string | null;
  url: string | null;
}

export interface StripeAccount {
  /** False when this Edvora environment has no Stripe key. */
  configured: boolean;
  customers: StripeCustomerRow[];
  invoices: StripeInvoiceRow[];
  totals: { customers: number; outstanding: number; openInvoices: number };
  /** Stripe has more than this page; the rest is in Stripe's own dashboard. */
  more: boolean;
  currency: string;
}

export const SALES_EMAIL = 'sales@edvoracampus.com';

export const PAYING_STATUSES: SubscriptionStatus[] = ['ACTIVE', 'TRIALING', 'PAST_DUE'];
export const paying = (status: SubscriptionStatus | null | undefined) => !!status && PAYING_STATUSES.includes(status);

/** Integrations that sync every hour or every 6 hours only with Frequent Integration Sync (as on the server). */
export const FREQUENT_SYNC_PROVIDERS: IntegrationProvider[] = ['BLACKBAUD', 'ONEROSTER', 'GOOGLE', 'JAMF_PRO', 'JAMF_SCHOOL', 'APPLE_SCHOOL_MANAGER', 'MOSYLE', 'INTUNE'];
/** A school's own sign-up trial syncs these integrations on demand only (as on the server). */
export const TRIAL_BLOCKED_FREQUENCIES: SyncFrequency[] = ['HOURLY', 'EVERY_6_HOURS', 'DAILY'];
/** The mailboxes: checked every few minutes on any plan, every minute only with the add-on (as on the server). */
export const MAILBOX_PROVIDERS: IntegrationProvider[] = ['EMAIL', 'FACILITIES_EMAIL'];
export const needsFrequentSync = (provider: IntegrationProvider, frequency: SyncFrequency) =>
  (FREQUENT_SYNC_PROVIDERS.includes(provider) && (frequency === 'HOURLY' || frequency === 'EVERY_6_HOURS')) ||
  (MAILBOX_PROVIDERS.includes(provider) && frequency === 'EVERY_MINUTE');

/** Integrations that are add-ons themselves. */
export const PROVIDER_FEATURE: Partial<Record<IntegrationProvider, BillingFeature>> = {
  GOOGLE_PROVISIONING: 'GOOGLE_PROVISIONING',
  BLACKBAUD_BILLING: 'BLACKBAUD_BILLING',
};

/** "$270.00" */
export const formatAmount = (cents: number, currency = 'usd') =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: currency.toUpperCase() }).format(cents / 100);

/** "up to 750 devices", or what to do when the fleet is bigger than anything sold self-serve. */
export function bandNote(item: Pick<CatalogItem, 'tier'>) {
  if (!item.tier) return null;
  const devices = item.tier.devices;
  const fleet = devices === null ? null : `${devices.toLocaleString()} Chromebook${devices === 1 ? '' : 's'}`;
  if (item.tier.upTo === null) return fleet ? `${fleet} - more than the bands cover, so email ${SALES_EMAIL} for a quote` : 'Priced by how many Chromebooks the school has';
  return `Up to ${item.tier.upTo.toLocaleString()} devices${fleet ? ` - the school has ${fleet}` : ''}`;
}

/** The school's fleet is past the largest band, so there's nothing to buy on the page. */
export const needsQuote = (item: Pick<CatalogItem, 'tier'>) => !!item.tier && item.tier.devices !== null && item.tier.upTo === null;

/** "$150/month" */
export function formatPrice(item: Pick<CatalogItem, 'amount' | 'currency' | 'interval'>) {
  const amount = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: item.currency.toUpperCase(),
    minimumFractionDigits: item.amount % 100 ? 2 : 0,
  }).format(item.amount / 100);
  return `${amount}/${item.interval}`;
}

/** The signed-in school's plan. Without a school, or before billing is set up, everything is included. */
export function usePlan() {
  const { me } = useAuth();
  const plan = me?.tenant?.billing;
  return {
    enforced: !!plan?.enforced,
    /** Plan & billing is worth showing: Stripe is set up, or billing is enforced. */
    visible: !!plan && (plan.enforced || plan.stripe),
    has: (feature: BillingFeature) => !plan || plan.features[feature],
    /** The school can't be used until its plan is renewed (Edvora's platform admins can still look around). */
    locked: !!plan && !plan.platform && !me?.user.isPlatformAdmin,
    /** Roster and device integrations sync on their schedules (mailboxes always do). */
    scheduledSyncs: !plan || plan.scheduledSyncs,
    /** On the free trial a school starts when it signs up itself. */
    signUpTrial: !!plan && plan.basis === 'TRIAL' && plan.trialKind === 'SIGN_UP',
    trialEndsAt: plan?.trialEndsAt ?? null,
    deletesAt: plan?.deletesAt ?? null,
    paymentIssues: plan?.paymentIssues ?? [],
  };
}
